import type RootStore from 'stores/RootStore';
import { RouteType } from 'types/RouteType';
import type DepartmentsStore from './DepartmentsStore';

const departmentsStoreOf = (rootStore: RootStore) =>
  (rootStore as unknown as { departmentAdminStore?: DepartmentsStore })
    .departmentAdminStore;

/**
 * A refused save THROWS, which keeps the dialog open with the API's message in
 * its footer and the form intact. Resolving closes it.
 */
export async function saveDepartmentOrThrow(rootStore: RootStore): Promise<void> {
  const store = departmentsStoreOf(rootStore);
  if (!(await store?.save())) {
    throw new Error(store?.saveError || 'The department could not be saved.');
  }
}

/**
 * Administration → Departments.
 *
 * Hand-built (Departments.Body / Departments.Header / DepartmentsStore) rather
 * than the library's inline-edit DataGrid, whose fixed-pixel columns (790px)
 * left a phone with under half the table and which had no way to add a
 * department (walkthrough 5×5 §4.2).
 */
const departmentsRoute: RouteType = {
  name: 'Departments',
  path: '/departments',
  layout: '/app',
  viewportCard: {
    header: {
      title: 'Departments',
      component: () => import('./Departments.Header'),
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import('./Departments.Body'),
    },
  },
  dialogs: [
    {
      name: 'DepartmentFormDialog',
      title: 'Department',
      component: () => import('./dialogs/DepartmentFormDialog.Body'),
      cancelButton: true,
      height: 420,
      submitButtonText: 'Save Department',
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        saveDepartmentOrThrow(rootStore),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
        departmentsStoreOf(rootStore)?.isFormValid === true,
    },
  ],
  group: 'Admin',
  searchAliases: ['org structure', 'teams', 'add department'],
  requiresAuth: true,
  requiresPermission: false,
  requiresAdmin: true,
  channels: ['departments'],
  // The route owns the load, through the view store, so a failure is RECORDED
  // (loadError → error + Retry) instead of swallowed by the ServiceStore. It
  // re-runs on every visit: the login page's pre-sign-in prefetch can leave a
  // stale "Not authenticated" on the ServiceStore, and only a fresh read of
  // THIS visit may decide what the page shows.
  enableAutoFind: false,
  onEnter: async ({ rootStore }) => {
    await departmentsStoreOf(rootStore)?.load();
  },
  hidden: false,
};

export default departmentsRoute;
